import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { AgGridReact } from 'ag-grid-react';
import { ColDef, ICellRendererParams, ModuleRegistry, AllCommunityModule, themeQuartz } from 'ag-grid-community';
import 'ag-grid-community/styles/ag-theme-material.css';
import '../styles/ag-grid-custom.css';
import { FaArrowLeft, FaUsers, FaTrophy, FaInfoCircle, FaList, FaUser, FaCrown, FaUserTie, FaLock, FaUnlock, FaCheck, FaTimes, FaCopy, FaEye, FaChevronDown, FaChevronRight, FaChartBar } from 'react-icons/fa';
import { FiX } from 'react-icons/fi';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import apiService from '../services/apiService';

interface LocationState {
  contest?: any;
}

// Register AG Grid modules
ModuleRegistry.registerModules([AllCommunityModule]);

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`contest-tabpanel-${index}`}
      aria-labelledby={`contest-tab-${index}`}
    >
      {value === index && <div className="p-6">{children}</div>}
    </div>
  );
}

const ContestDetails: React.FC = () => {
  const { contestId } = useParams<{ contestId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const locationState = location.state as LocationState;

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [contest, setContest] = useState<any>(null);
  const [tabValue, setTabValue] = useState(0);
  const [contestMembers, setContestMembers] = useState<any[]>([]);
  const [contestLeaderboard, setContestLeaderboard] = useState<any[]>([]);
  const [loadingMembers, setLoadingMembers] = useState(false);
  const [loadingLeaderboard, setLoadingLeaderboard] = useState(false);
  const [selectedMember, setSelectedMember] = useState<any>(null);
  const [selectedLeaderboardEntry, setSelectedLeaderboardEntry] = useState<any>(null);
  const [fantasyTeams, setFantasyTeams] = useState<any[]>([]);
  const [loadingFantasyTeams, setLoadingFantasyTeams] = useState(false);
  const [expandedTeams, setExpandedTeams] = useState<Set<string>>(new Set());
  const [reassignTeam, setReassignTeam] = useState<any>(null);
  const [reassignUserId, setReassignUserId] = useState('');
  const [reassigning, setReassigning] = useState(false);
  const [playerTeamMap, setPlayerTeamMap] = useState<Map<string, { teamId: string; teamName: string; teamShortName: string; playerName: string; role: string }>>(new Map());
  const [playerScores, setPlayerScores] = useState<any[]>([]);
  const [loadingPlayerScores, setLoadingPlayerScores] = useState(false);
  const [playerScoreDetailsModalOpen, setPlayerScoreDetailsModalOpen] = useState(false);
  const [selectedPlayerScore, setSelectedPlayerScore] = useState<any>(null);
  const [seriesTeams, setSeriesTeams] = useState<any[]>([]);
  const [selectedPlayerScoreTeamFilter, setSelectedPlayerScoreTeamFilter] = useState<number | null>(null);
  const [fantasyPointsBreakdown, setFantasyPointsBreakdown] = useState<any[]>([]);
  const [loadingFantasyPoints, setLoadingFantasyPoints] = useState(false);
  const [selectedFantasyPointsPlayer, setSelectedFantasyPointsPlayer] = useState<any>(null);
  const [fantasyPointsDetailModalOpen, setFantasyPointsDetailModalOpen] = useState(false);
  const [selectedFPSquadFilter, setSelectedFPSquadFilter] = useState<string>('');

  // Match Clash state
  const [seriesMatches, setSeriesMatches] = useState<any[]>([]);
  const [selectedMatchFilter, setSelectedMatchFilter] = useState<string>('all');
  const [matchLeaderboard, setMatchLeaderboard] = useState<any[]>([]);
  const [loadingMatchLeaderboard, setLoadingMatchLeaderboard] = useState(false);

  useEffect(() => {
    // First try to use contest from location state
    if (locationState?.contest) {
      console.log('Using contest from location state:', locationState.contest);
      setContest(locationState.contest);
      setLoading(false);
    } else if (contestId) {
      // Fallback to API call if no state
      loadContestDetails();
    } else {
      setLoading(false);
      setError('Contest not found');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contestId, locationState]);

  // Load matches for match_clash contests
  const isMatchClash = contest?.contestType === 'match_clash';
  useEffect(() => {
    if (isMatchClash && contest?.seriesId && seriesMatches.length === 0) {
      apiService.getMatches(String(contest.seriesId)).then((data) => {
        const sorted = (data || [])
          .filter((m: any) => m.state !== 'Cancelled')
          .sort((a: any, b: any) => (a.matchOrderId || 0) - (b.matchOrderId || 0));
        setSeriesMatches(sorted);
      }).catch(() => {});
    }
  }, [isMatchClash, contest?.seriesId]);

  // Load match-specific leaderboard when filter changes
  useEffect(() => {
    if (!isMatchClash || selectedMatchFilter === 'all') {
      setMatchLeaderboard([]);
      return;
    }
    const id = contest?._id || contest?.id || contestId;
    if (!id) return;
    setLoadingMatchLeaderboard(true);
    apiService.getContestLeaderboard(id, Number(selectedMatchFilter)).then((data) => {
      setMatchLeaderboard(data || []);
    }).catch(() => {
      setMatchLeaderboard([]);
    }).finally(() => {
      setLoadingMatchLeaderboard(false);
    });
  }, [isMatchClash, selectedMatchFilter, contest, contestId]);

  const loadContestDetails = async () => {
    try {
      setLoading(true);
      const contestData = await apiService.getContestById(contestId!);
      console.log('Contest fetched by ID:', contestData);
      setContest(contestData);
    } catch (err) {
      console.error('Error fetching contest by ID:', err);
      setError('Failed to fetch contest details. Please navigate from the contests list.');
    } finally {
      setLoading(false);
    }
  };

  const loadContestMembers = async () => {
    const id = contest?._id || contest?.id || contestId;
    if (!id) {
      console.error('No contest ID available for fetching members');
      return;
    }
    if (contestMembers.length > 0) return;
    try {
      setLoadingMembers(true);
      console.log('Fetching members for contest:', id);
      const data = await apiService.getContestMembers(id);
      console.log('Contest members response:', data);
      console.log('Members count:', data?.length || 0);
      if (data && data.length > 0) {
        console.log('First member keys:', Object.keys(data[0]));
        console.log('First member:', data[0]);
      }
      setContestMembers(data || []);
    } catch (err: any) {
      console.error('Error fetching contest members:', err);
      toast.error(`Failed to load members: ${err.message || 'Unknown error'}`);
    } finally {
      setLoadingMembers(false);
    }
  };

  const loadContestLeaderboard = async () => {
    const id = contest?._id || contest?.id || contestId;
    if (!id) {
      console.error('No contest ID available for fetching leaderboard');
      return;
    }
    if (contestLeaderboard.length > 0) return;
    try {
      setLoadingLeaderboard(true);
      console.log('Fetching leaderboard for contest:', id);
      const data = await apiService.getContestLeaderboard(id);
      console.log('Contest leaderboard response:', data);
      console.log('Leaderboard count:', data?.length || 0);
      if (data && data.length > 0) {
        console.log('First leaderboard entry keys:', Object.keys(data[0]));
        console.log('First leaderboard entry:', data[0]);
      }
      setContestLeaderboard(data || []);
    } catch (err: any) {
      console.error('Error fetching contest leaderboard:', err);
      toast.error(`Failed to load leaderboard: ${err.message || 'Unknown error'}`);
    } finally {
      setLoadingLeaderboard(false);
    }
  };

  const loadFantasyTeams = async () => {
    const id = contest?._id || contest?.id || contestId;
    if (!id) return;
    if (fantasyTeams.length > 0) return;
    try {
      setLoadingFantasyTeams(true);
      const data = await apiService.getContestFantasyTeams(id);
      console.log('=== Fantasy Teams API Response ===');
      console.log('Raw data:', data);
      console.log('Type:', typeof data, 'isArray:', Array.isArray(data));
      if (Array.isArray(data) && data.length > 0) {
        console.log('First team keys:', Object.keys(data[0]));
        console.log('First team:', JSON.stringify(data[0], null, 2));
      } else if (data && typeof data === 'object' && !Array.isArray(data)) {
        console.log('Data is object, keys:', Object.keys(data));
      }
      console.log('=== End Fantasy Teams Debug ===');
      setFantasyTeams(Array.isArray(data) ? data : ((data as any)?.teams || (data as any)?.data || []));

      // Fetch squads + teams separately so a failure doesn't block fantasy teams
      const seriesId = contest?.seriesId;
      if (seriesId) {
        try {
          const [squadsData, teamsData]: [any[], any[]] = await Promise.all([
            apiService.getSquadsBySeries(String(seriesId)),
            apiService.getTeamsBySeries(String(seriesId)),
          ]);
          if (teamsData) {
            setSeriesTeams(teamsData);
          }
          // Build player-to-team lookup map
          // squadsData is a flat array of players, each with playerId, teamId, teamInfo
          // teamsData is an array of { teamId, teamName, ... }
          if (squadsData && teamsData) {
            const teamLookup = new Map<string, { name: string; shortName: string }>();
            for (const team of teamsData) {
              teamLookup.set(String(team.teamId || team.id), { name: team.teamName || team.name || '', shortName: team.shortName || '' });
            }
            const pMap = new Map<string, { teamId: string; teamName: string; teamShortName: string; playerName: string; role: string }>();
            for (const player of squadsData) {
              // Each player has playerId, teamId, teamInfo, playerName, playerRole etc.
              const playerId = String(player.playerId || player.id || '');
              const teamId = String(player.teamId || '');
              const teamInfo = teamLookup.get(teamId);
              const teamName = player.teamInfo?.teamName || teamInfo?.name || '';
              const teamShortName = player.teamInfo?.shortName || teamInfo?.shortName || '';
              const playerName = player.playerName || player.name || '';
              const role = player.playerRole || player.role || '';
              pMap.set(playerId, { teamId, teamName, teamShortName, playerName, role });
            }
            setPlayerTeamMap(pMap);
          }
        } catch (squadErr) {
          console.error('Error loading squads/teams:', squadErr);
        }
      }
    } catch (err: any) {
      console.error('Error loading fantasy teams:', err);
      toast.error(`Failed to load fantasy teams: ${err.message || 'Unknown error'}`);
    } finally {
      setLoadingFantasyTeams(false);
    }
  };

  const loadPlayerScores = async () => {
    const seriesId = contest?.seriesId;
    if (!seriesId || playerScores.length > 0) return;
    try {
      setLoadingPlayerScores(true);
      const [scoresData, teamsData] = await Promise.all([
        apiService.getPlayerScores(String(seriesId)),
        seriesTeams.length > 0 ? Promise.resolve(seriesTeams) : apiService.getTeamsBySeries(String(seriesId)),
      ]);
      setPlayerScores(scoresData || []);
      if (teamsData && teamsData !== seriesTeams) {
        setSeriesTeams(teamsData);
      }
    } catch (err: any) {
      console.error('Error fetching player scores:', err);
      toast.error(`Failed to load player scores: ${err.message || 'Unknown error'}`);
    } finally {
      setLoadingPlayerScores(false);
    }
  };

  const flattenedPlayerScores = useMemo(() => {
    const filteredScores = playerScores.filter((player) =>
      selectedPlayerScoreTeamFilter === null || player.teamId === selectedPlayerScoreTeamFilter
    );

    return filteredScores.map((player) => {
      if (!player.matches || !Array.isArray(player.matches) || player.matches.length === 0) {
        return {
          ...player,
          matchesPlayed: 0,
          runsScored: 0,
          ballsFaced: 0,
          fours: 0,
          sixes: 0,
          wickets: 0,
          overs: 0,
          runsConceded: 0,
          catches: 0,
          stumps: 0,
          runouts: 0,
          mom: 0,
          battingAvg: '0.00',
          bowlingAvg: '-',
          economy: '-',
        };
      }

      const totalStats = player.matches.reduce(
        (acc: any, match: any) => {
          const breakdown = match.scoresBreakdown || {};
          return {
            matchesPlayed: acc.matchesPlayed + 1,
            runsScored: acc.runsScored + (breakdown.runsScored || 0),
            ballsFaced: acc.ballsFaced + (breakdown.ballsFaced || 0),
            fours: acc.fours + (breakdown.fours || 0),
            sixes: acc.sixes + (breakdown.sixes || 0),
            wickets: acc.wickets + (breakdown.wickets || 0),
            overs: acc.overs + (breakdown.overs || 0),
            runsConceded: acc.runsConceded + (breakdown.runsConceded || 0),
            catches: acc.catches + (breakdown.catch || 0),
            stumps: acc.stumps + (breakdown.stump || 0),
            runouts: acc.runouts + (breakdown.runouts || 0),
            mom: acc.mom + (breakdown.mom || 0),
          };
        },
        { matchesPlayed: 0, runsScored: 0, ballsFaced: 0, fours: 0, sixes: 0, wickets: 0, overs: 0, runsConceded: 0, catches: 0, stumps: 0, runouts: 0, mom: 0 }
      );

      const battingAvg = totalStats.ballsFaced > 0 ? (totalStats.runsScored / totalStats.ballsFaced) * 100 : 0;
      const bowlingAvg = totalStats.wickets > 0 ? totalStats.runsConceded / totalStats.wickets : 0;
      const economy = totalStats.overs > 0 ? totalStats.runsConceded / totalStats.overs : 0;

      return {
        ...player,
        matches: player.matches,
        ...totalStats,
        battingAvg: battingAvg.toFixed(2),
        bowlingAvg: bowlingAvg > 0 ? bowlingAvg.toFixed(2) : '-',
        economy: economy > 0 ? economy.toFixed(2) : '-',
      };
    });
  }, [playerScores, selectedPlayerScoreTeamFilter]);

  const handlePlayerScoreRowClick = (event: any) => {
    if (event.data) {
      setSelectedPlayerScore(event.data);
      setPlayerScoreDetailsModalOpen(true);
    }
  };

  const playerScoresColumns: ColDef[] = useMemo(() => {
    const getTeamName = (teamId: number) => {
      const team = seriesTeams?.find((t: any) => t.teamId === teamId);
      return team?.teamName || `Team ${teamId}`;
    };

    return [
      { field: 'playerId', headerName: 'Player ID', width: 120, filter: 'agNumberColumnFilter', pinned: 'left' as const },
      { field: 'playerName', headerName: 'Player', flex: 1, minWidth: 200, filter: 'agTextColumnFilter', pinned: 'left' as const },
      { field: 'teamId', headerName: 'Team', width: 150, filter: 'agTextColumnFilter', valueGetter: (params: any) => getTeamName(params.data?.teamId) },
      { field: 'matchesPlayed', headerName: 'Matches', width: 90, filter: 'agNumberColumnFilter' },
      { field: 'runsScored', headerName: 'Runs', width: 100, filter: 'agNumberColumnFilter', cellStyle: { fontWeight: 600 } },
      { field: 'ballsFaced', headerName: 'Balls', width: 100, filter: 'agNumberColumnFilter' },
      { field: 'battingAvg', headerName: 'Strike Rate', width: 110, filter: 'agNumberColumnFilter' },
      { field: 'fours', headerName: '4s', width: 80, filter: 'agNumberColumnFilter' },
      { field: 'sixes', headerName: '6s', width: 80, filter: 'agNumberColumnFilter' },
      { field: 'wickets', headerName: 'Wickets', width: 100, filter: 'agNumberColumnFilter', cellStyle: { fontWeight: 600 } },
      { field: 'overs', headerName: 'Overs', width: 100, filter: 'agNumberColumnFilter' },
      { field: 'runsConceded', headerName: 'Runs Conc.', width: 110, filter: 'agNumberColumnFilter' },
      { field: 'economy', headerName: 'Economy', width: 100, filter: 'agNumberColumnFilter' },
      { field: 'catches', headerName: 'Catches', width: 100, filter: 'agNumberColumnFilter' },
      { field: 'stumps', headerName: 'Stumps', width: 100, filter: 'agNumberColumnFilter' },
      { field: 'runouts', headerName: 'Runouts', width: 100, filter: 'agNumberColumnFilter' },
    ];
  }, [seriesTeams]);

  const loadFantasyPointsBreakdown = async () => {
    const id = contest?._id || contest?.id || contestId;
    const seriesId = contest?.seriesId;
    if (!id) return;
    if (fantasyPointsBreakdown.length > 0) return;
    try {
      setLoadingFantasyPoints(true);

      // Fetch breakdown, squads, teams, and matches in parallel
      const [data, squadsData, teamsData, matchesData]: [any, any[], any[], any[]] = await Promise.all([
        apiService.getContestFantasyPointsBreakdown(id, { groupBySquad: true }),
        seriesId ? apiService.getSquadsBySeries(String(seriesId)).catch(() => []) : Promise.resolve([]),
        seriesId ? apiService.getTeamsBySeries(String(seriesId)).catch(() => []) : Promise.resolve([]),
        seriesId ? apiService.getMatches(String(seriesId)).catch(() => []) : Promise.resolve([]),
      ]);

      // squadsData = flat array of players: { playerId, playerName, playerRole, teamId, teamInfo: { teamName } }
      // teamsData = array of teams: { teamId, teamName }
      // matchesData = array of matches: { matchId, localTeam: { name }, visitorTeam: { name }, venueInfo: { ground, city } }

      // Build team lookup: teamId -> teamName
      const teamLookup = new Map<string, string>();
      if (teamsData) {
        for (const t of teamsData) {
          teamLookup.set(String(t.teamId || t.id), t.teamName || t.name || '');
        }
      }

      // Build player lookup from squads (flat player array): playerId -> { playerName, role, teamId, teamName }
      const squadPlayerMap = new Map<string, any>();
      if (squadsData) {
        for (const p of squadsData) {
          const pId = String(p.playerId || p.id || '');
          const tId = String(p.teamId || '');
          squadPlayerMap.set(pId, {
            playerName: p.playerName || p.name || '',
            role: p.playerRole || p.role || '',
            teamId: tId,
            teamName: p.teamInfo?.teamName || teamLookup.get(tId) || '',
          });
        }
      }

      // Build match lookup: matchId -> match info
      const matchMap = new Map<string, any>();
      if (matchesData) {
        for (const m of matchesData) {
          matchMap.set(String((m as any).matchId || m.id), m);
        }
      }

      // Helper to enrich a match entry with data from matchMap
      const enrichMatch = (match: any) => {
        const mId = String(match.matchId || '');
        const matchInfo = matchMap.get(mId);
        const localTeam = matchInfo?.localTeam?.name || matchInfo?.localTeam?.teamName || matchInfo?.localTeam || '';
        const visitorTeam = matchInfo?.visitorTeam?.name || matchInfo?.visitorTeam?.teamName || matchInfo?.visitorTeam || '';
        const venueGround = matchInfo?.venueInfo?.ground || matchInfo?.venue || '';
        const venueCity = matchInfo?.venueInfo?.city || '';
        const venue = venueCity ? `${venueGround}, ${venueCity}` : venueGround;
        return {
          ...match,
          matchTitle: match.matchTitle || match.title || (localTeam && visitorTeam ? `${localTeam} vs ${visitorTeam}` : '') || `Match ${mId}`,
          venue,
          date: matchInfo?.startDate || matchInfo?.date || match.date || '',
          team1: localTeam,
          team2: visitorTeam,
        };
      };

      // Normalize data to array
      let dataArray: any[] = [];
      if (Array.isArray(data)) {
        dataArray = data;
      } else if (data && typeof data === 'object') {
        dataArray = data.players || data.breakdown || data.data || [];
        if (!Array.isArray(dataArray)) dataArray = [data];
      }

      // Flatten and enrich players
      // API returns: [{ squadId, teamId, players: [{ playerId, playerInfo, pointsBreakdown, fixtureTotalPoints }] }]
      let players: any[] = [];
      for (const item of dataArray) {
        if (item.players && Array.isArray(item.players)) {
          const sId = String(item.squadId || item.teamId || '');
          const squadTeamName = teamLookup.get(String(item.teamId)) || '';
          for (const p of item.players) {
            const pId = String(p.playerId || '');
            const sqPlayer = squadPlayerMap.get(pId);
            const pInfo = p.playerInfo || {};
            const matchBreakdowns = p.pointsBreakdown || p.matches || [];

            // Compute section totals across all matches
            let battingTotal = 0, bowlingTotal = 0, fieldingTotal = 0, otherTotal = 0;
            const enrichedMatches = matchBreakdowns.map((mb: any) => {
              const mId = String(mb.matchId || '');
              const matchInfo = matchMap.get(mId);
              const localTeam = matchInfo?.localTeam?.teamName || matchInfo?.localTeam?.name || '';
              const localTeamShort = matchInfo?.localTeam?.teamSName || '';
              const visitorTeam = matchInfo?.visitorTeam?.teamName || matchInfo?.visitorTeam?.name || '';
              const visitorTeamShort = matchInfo?.visitorTeam?.teamSName || '';
              const venueGround = matchInfo?.venueInfo?.ground || '';
              const venueCity = matchInfo?.venueInfo?.city || '';
              const venue = venueCity ? `${venueGround}, ${venueCity}` : venueGround;
              const matchDesc = matchInfo?.matchDesc || '';

              // breakdown is array of sections: [{ section, fields, total }]
              const sections = mb.breakdown || [];
              let mBatting = 0, mBowling = 0, mFielding = 0, mOther = 0;
              for (const sec of sections) {
                const sectionName = (sec.section || '').toLowerCase();
                if (sectionName.includes('batting')) mBatting = sec.total || 0;
                else if (sectionName.includes('bowling')) mBowling = sec.total || 0;
                else if (sectionName.includes('fielding')) mFielding = sec.total || 0;
                else mOther += sec.total || 0;
              }
              battingTotal += mBatting;
              bowlingTotal += mBowling;
              fieldingTotal += mFielding;
              otherTotal += mOther;

              return {
                matchId: mId,
                totalPoints: mb.totalPoints ?? 0,
                sections,
                matchTitle: localTeam && visitorTeam
                  ? `${localTeamShort || localTeam} vs ${visitorTeamShort || visitorTeam}`
                  : matchDesc || `Match ${mId}`,
                matchDesc,
                team1: localTeam,
                team1Short: localTeamShort,
                team2: visitorTeam,
                team2Short: visitorTeamShort,
                venue,
                date: matchInfo?.startDate || '',
                status: matchInfo?.status || '',
                battingPoints: mBatting,
                bowlingPoints: mBowling,
                fieldingPoints: mFielding,
                otherPoints: mOther,
              };
            });

            const totalPoints = p.fixtureTotalPoints ?? enrichedMatches.reduce((s: number, m: any) => s + (m.totalPoints || 0), 0);
            const matchesPlayed = enrichedMatches.length;

            players.push({
              playerId: p.playerId,
              playerName: pInfo.playerName || sqPlayer?.playerName || pId,
              role: pInfo.playerRole || sqPlayer?.role || '',
              squadId: sId,
              squadName: squadTeamName || sqPlayer?.teamName || '',
              teamId: pInfo.teamId || item.teamId,
              totalPoints,
              matchesPlayed,
              avgPoints: matchesPlayed > 0 ? Math.round((totalPoints / matchesPlayed) * 100) / 100 : 0,
              battingPoints: battingTotal,
              bowlingPoints: bowlingTotal,
              fieldingPoints: fieldingTotal,
              otherPoints: otherTotal,
              matches: enrichedMatches,
            });
          }
        } else {
          // Flat player entry fallback
          const pId = String(item.playerId || '');
          const sqPlayer = squadPlayerMap.get(pId);
          const pInfo = item.playerInfo || {};
          const tId = String(item.squadId || item.teamId || sqPlayer?.teamId || '');
          players.push({
            ...item,
            playerName: pInfo.playerName || item.playerName || sqPlayer?.playerName || pId,
            role: pInfo.playerRole || item.role || sqPlayer?.role || '',
            squadId: tId,
            squadName: teamLookup.get(tId) || sqPlayer?.teamName || '',
            totalPoints: item.fixtureTotalPoints ?? item.totalPoints ?? 0,
            matchesPlayed: (item.pointsBreakdown || item.matches || []).length,
            matches: item.pointsBreakdown || item.matches || [],
          });
        }
      }

      setFantasyPointsBreakdown(players);
    } catch (err: any) {
      console.error('Error loading fantasy points breakdown:', err);
      toast.error(`Failed to load fantasy points: ${err.message || 'Unknown error'}`);
    } finally {
      setLoadingFantasyPoints(false);
    }
  };

  const filteredFantasyPoints = useMemo(() => {
    if (!selectedFPSquadFilter) return fantasyPointsBreakdown;
    return fantasyPointsBreakdown.filter((p) => String(p.squadId || p.teamId) === selectedFPSquadFilter);
  }, [fantasyPointsBreakdown, selectedFPSquadFilter]);

  const fpSquads = useMemo(() => {
    const map = new Map<string, string>();
    for (const p of fantasyPointsBreakdown) {
      const id = String(p.squadId || p.teamId || '');
      if (id && !map.has(id)) {
        map.set(id, p.squadName || p.teamName || `Squad ${id}`);
      }
    }
    return Array.from(map.entries()).map(([id, name]) => ({ id, name }));
  }, [fantasyPointsBreakdown]);

  const handleFPRowClick = (event: any) => {
    if (event.data) {
      setSelectedFantasyPointsPlayer(event.data);
      setFantasyPointsDetailModalOpen(true);
    }
  };

  const fantasyPointsColumns: ColDef[] = useMemo(() => [
    { field: 'playerId', headerName: 'ID', flex: 0.6, minWidth: 70, filter: 'agNumberColumnFilter', pinned: 'left' as const },
    { field: 'playerName', headerName: 'Player', flex: 1.4, minWidth: 130, filter: 'agTextColumnFilter', pinned: 'left' as const },
    { field: 'squadName', headerName: 'Team', flex: 1, minWidth: 100, filter: 'agTextColumnFilter' },
    { field: 'role', headerName: 'Role', flex: 0.8, minWidth: 90, filter: 'agTextColumnFilter',
      cellRenderer: (params: ICellRendererParams) => {
        const role = params.value || '';
        const colorMap: Record<string, string> = {
          'Batsman': 'bg-blue-100 text-blue-800 dark:bg-blue-500/20 dark:text-blue-400',
          'Bowler': 'bg-green-100 text-green-800 dark:bg-green-500/20 dark:text-green-400',
          'Allrounder': 'bg-purple-100 text-purple-800 dark:bg-purple-500/20 dark:text-purple-400',
          'WK-Batsman': 'bg-orange-100 text-orange-800 dark:bg-orange-500/20 dark:text-orange-400',
        };
        return role ? (
          <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-semibold ${colorMap[role] || 'bg-gray-100 text-gray-800'}`}>
            {role}
          </span>
        ) : '-';
      },
    },
    {
      field: 'totalPoints',
      headerName: 'Total Pts',
      flex: 0.7,
      minWidth: 80,
      filter: 'agNumberColumnFilter',
      sort: 'desc' as const,
      cellRenderer: (params: ICellRendererParams) => {
        const val = params.value ?? 0;
        return (
          <span className={`font-bold text-sm ${val > 0 ? 'text-green-700 dark:text-green-400' : val < 0 ? 'text-red-600' : 'text-gray-500'}`}>
            {val}
          </span>
        );
      },
    },
    { field: 'matchesPlayed', headerName: 'Matches', flex: 0.6, minWidth: 70, filter: 'agNumberColumnFilter' },
    { field: 'avgPoints', headerName: 'Avg Pts', flex: 0.6, minWidth: 70, filter: 'agNumberColumnFilter' },
    {
      field: 'battingPoints',
      headerName: 'Batting',
      flex: 0.6,
      minWidth: 70,
      filter: 'agNumberColumnFilter',
      cellStyle: (params: any) => (params.value > 0 ? { color: '#2563eb' } : null),
    },
    {
      field: 'bowlingPoints',
      headerName: 'Bowling',
      flex: 0.6,
      minWidth: 70,
      filter: 'agNumberColumnFilter',
      cellStyle: (params: any) => (params.value > 0 ? { color: '#16a34a' } : null),
    },
    {
      field: 'fieldingPoints',
      headerName: 'Fielding',
      flex: 0.6,
      minWidth: 70,
      filter: 'agNumberColumnFilter',
      cellStyle: (params: any) => (params.value > 0 ? { color: '#9333ea' } : null),
    },
    {
      field: 'otherPoints',
      headerName: 'Bonus',
      flex: 0.6,
      minWidth: 70,
      filter: 'agNumberColumnFilter',
      cellStyle: (params: any) => (params.value > 0 ? { color: '#d97706' } : null),
    },
  ], []);

  const toggleTeamExpand = (teamId: string) => {
    setExpandedTeams(prev => {
      const next = new Set(prev);
      if (next.has(teamId)) {
        next.delete(teamId);
      } else {
        next.add(teamId);
      }
      return next;
    });
  };

  const handleTabChange = (newValue: number) => {
    setTabValue(newValue);
    if (newValue === 3) {
      loadFantasyTeams();
      // Members are needed to resolve team-owner display names on
      // each team card; load them eagerly when Teams tab opens.
      loadContestMembers();
    } else if (newValue === 4) {
      loadContestMembers();
    } else if (newValue === 5) {
      loadContestLeaderboard();
    } else if (newValue === 6) {
      loadPlayerScores();
    } else if (newValue === 7) {
      loadFantasyPointsBreakdown();
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard!');
  };

  // Format date helper
  const formatDate = (dateStr: string) => {
    if (!dateStr) return 'N/A';
    try {
      return new Date(dateStr).toLocaleString();
    } catch {
      return dateStr;
    }
  };

  // Members grid columns - simplified view
  const memberColumns: ColDef[] = useMemo(() => {
    const columns: ColDef[] = [
      {
        field: 'name',
        headerName: 'Name',
        flex: 1,
        minWidth: 150,
        valueGetter: (params) => {
          const data = params.data;
          if (!data) return 'N/A';
          const first = data.firstname || data.firstName || data.first_name || '';
          const last = data.lastname || data.lastName || data.last_name || '';
          const fullName = `${first} ${last}`.trim();
          return fullName || data.name || data.userName || data.username || 'N/A';
        }
      },
      {
        field: 'userId',
        headerName: 'User ID',
        flex: 1,
        minWidth: 240,
        valueGetter: (params) => params.data?.userId || params.data?.user_id || 'N/A',
        cellRenderer: (params: ICellRendererParams) => (
          <span className="font-mono text-xs text-gray-700 dark:text-gray-300">{params.value}</span>
        ),
      },
      {
        field: 'email',
        headerName: 'Email',
        flex: 1.5,
        minWidth: 200,
        valueGetter: (params) => params.data?.email || params.data?.userEmail || 'N/A'
      },
      {
        field: 'joinedAt',
        headerName: 'Joined At',
        flex: 1,
        minWidth: 160,
        valueGetter: (params) => {
          const data = params.data;
          if (!data) return 'N/A';
          const dateVal = data.joinedAt || data.joined_at || data.createdAt || data.created_at || data.addedAt || data.added_at;
          return dateVal ? formatDate(dateVal) : 'N/A';
        }
      },
      {
        field: 'status',
        headerName: 'Status',
        width: 120,
        valueGetter: (params) => params.data?.status || params.data?.memberStatus || 'pending',
        cellRenderer: (params: ICellRendererParams) => {
          const status = params.value || 'pending';
          const statusLower = String(status).toLowerCase();
          const isAccepted = statusLower === 'accepted' || statusLower === 'active' || statusLower === 'joined';
          return (
            <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-semibold border ${
              isAccepted ? 'bg-green-100 text-green-800 border-green-300' : 'bg-yellow-100 text-yellow-800 border-yellow-300'
            }`}>
              {status}
            </span>
          );
        }
      },
      {
        field: 'actions',
        headerName: 'Actions',
        width: 100,
        sortable: false,
        filter: false,
        cellRenderer: (params: ICellRendererParams) => (
          <button
            onClick={() => setSelectedMember(params.data)}
            className="inline-flex items-center px-3 py-1 bg-blue-50 text-blue-700 rounded-md hover:bg-blue-100 transition-colors text-xs font-medium"
          >
            <FaEye className="mr-1 h-3 w-3" />
            View
          </button>
        )
      }
    ];
    return columns;
  }, []);

  // Leaderboard grid columns - simplified view
  const leaderboardColumns: ColDef[] = useMemo(() => {
    const columns: ColDef[] = [
      {
        field: 'rank',
        headerName: 'Rank',
        width: 80,
        cellRenderer: (params: ICellRendererParams) => {
          const rank = params.data?.rank || (params.node?.rowIndex ?? 0) + 1;
          return (
            <div className="flex items-center justify-center">
              {rank <= 3 ? (
                <span className={`inline-flex items-center justify-center w-8 h-8 rounded-full text-white font-bold ${
                  rank === 1 ? 'bg-yellow-500' : rank === 2 ? 'bg-gray-400' : 'bg-amber-600'
                }`}>
                  {rank}
                </span>
              ) : (
                <span className="font-semibold text-gray-700">{rank}</span>
              )}
            </div>
          );
        }
      },
      {
        field: 'teamName',
        headerName: 'Team Name',
        flex: 1.5,
        minWidth: 150,
        valueGetter: (params) => {
          if (!params.data) return 'N/A';
          return params.data.fantasyTeam?.fantasyName || params.data.teamName || 'N/A';
        }
      },
      {
        field: 'owner',
        headerName: 'Owner',
        flex: 1.5,
        minWidth: 150,
        valueGetter: (params) => {
          if (!params.data) return 'N/A';
          const ownerInfo = params.data.ownerInfo;
          if (ownerInfo) {
            const first = ownerInfo.firstname || ownerInfo.firstName || '';
            const last = ownerInfo.lastname || ownerInfo.lastName || '';
            return `${first} ${last}`.trim() || ownerInfo.email || 'N/A';
          }
          return 'N/A';
        }
      },
      {
        field: 'totalPoints',
        headerName: 'Points',
        width: 100,
        cellRenderer: (params: ICellRendererParams) => (
          <span className="font-bold text-blue-600">{params.data?.totalPoints ?? 0}</span>
        )
      },
      {
        field: 'pointsDifference',
        headerName: 'Diff',
        width: 80,
        cellRenderer: (params: ICellRendererParams) => {
          const diff = params.data?.pointsDifference || 0;
          if (diff === 0) {
            return <span className="text-gray-400">-</span>;
          }
          return <span className="text-red-500">-{diff}</span>;
        }
      },
      {
        field: 'actions',
        headerName: 'Actions',
        width: 100,
        sortable: false,
        filter: false,
        cellRenderer: (params: ICellRendererParams) => (
          <button
            onClick={() => setSelectedLeaderboardEntry(params.data)}
            className="inline-flex items-center px-3 py-1 bg-blue-50 text-blue-700 rounded-md hover:bg-blue-100 transition-colors text-xs font-medium"
          >
            <FaEye className="mr-1 h-3 w-3" />
            View
          </button>
        )
      }
    ];
    return columns;
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600 dark:text-gray-400">Loading contest details...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 dark:bg-red-500/20 border border-red-200 dark:border-red-500/30 rounded-lg p-4 text-center">
          <p className="text-red-800 dark:text-red-400">{error}</p>
          <button
            onClick={() => navigate(-1)}
            className="mt-4 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  if (!contest) {
    return (
      <div className="p-6">
        <div className="text-center text-gray-600 dark:text-gray-400">
          <p>Contest not found</p>
          <button
            onClick={() => navigate(-1)}
            className="mt-4 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  const ownerInfo = contest.ownerInfo?.[0] || {};

  return (
    <div className="space-y-6">
      <ToastContainer position="top-right" autoClose={3000} />

      {/* Header with Back Button */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate(-1)}
            className="p-2 rounded-lg bg-gray-100 dark:bg-dark-300/40 hover:bg-gray-200 dark:hover:bg-dark-300/60 transition-colors"
          >
            <FaArrowLeft className="h-5 w-5 text-gray-600 dark:text-gray-400" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-3">
              {contest.name}
              {contest.contestType && (
                <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold ${
                  contest.contestType === 'match_clash'
                    ? 'bg-amber-100 text-amber-800 dark:bg-amber-500/20 dark:text-amber-400'
                    : contest.contestType === 'daily_clash'
                    ? 'bg-blue-100 text-blue-800 dark:bg-blue-500/20 dark:text-blue-400'
                    : 'bg-green-100 text-green-800 dark:bg-green-500/20 dark:text-green-400'
                }`}>
                  {contest.contestType === 'match_clash' ? 'Match Clash' : contest.contestType === 'daily_clash' ? 'Daily Clash' : 'Series Clash'}
                </span>
              )}
              {contest.isPrivate && (
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold bg-purple-100 text-purple-800 dark:bg-purple-500/20 dark:text-purple-400">
                  <FaLock className="mr-1 h-3 w-3" />
                  Private
                </span>
              )}
            </h1>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              {contest.seriesName || 'Unknown Series'}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 px-4 py-2 bg-blue-50 dark:bg-blue-500/20 rounded-lg border border-blue-200 dark:border-blue-500/30">
            <span className="text-sm text-blue-700 dark:text-blue-400 font-medium">Contest Code:</span>
            <span className="font-mono font-bold text-blue-800 dark:text-blue-300">{contest.contestCode}</span>
            <button
              onClick={() => copyToClipboard(contest.contestCode)}
              className="p-1 hover:bg-blue-100 dark:hover:bg-blue-500/30 rounded transition-colors"
            >
              <FaCopy className="h-4 w-4 text-blue-600 dark:text-blue-400" />
            </button>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white dark:bg-dark-400/60 backdrop-blur-sm rounded-xl border border-gray-200 dark:border-dark-300/40 shadow-lg overflow-hidden">
        <div className="flex border-b border-gray-200 dark:border-dark-300/40 overflow-x-auto">
          {['Overview', 'Rules Config', 'Scoring Config', 'Teams', 'Members', 'Leaderboard', 'Player Scores', 'Fantasy Points'].map((tab, index) => (
            <button
              key={tab}
              onClick={() => handleTabChange(index)}
              className={`px-6 py-4 text-sm font-medium whitespace-nowrap transition-colors ${
                tabValue === index
                  ? 'text-blue-600 dark:text-primary-400 border-b-2 border-blue-600 dark:border-primary-400 bg-blue-50/50 dark:bg-primary-500/10'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-50 dark:hover:bg-dark-300/40'
              }`}
            >
              {tab}
            </button>
          ))}
        </div>

        {/* Tab Panels */}
        <TabPanel value={tabValue} index={0}>
          {/* Overview Tab */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Basic Info Card */}
            <div className="bg-gray-50 dark:bg-dark-300/40 rounded-lg p-6 border border-gray-200 dark:border-dark-300/40">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                <FaInfoCircle className="text-blue-500" />
                Basic Information
              </h3>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-gray-600 dark:text-gray-400">Contest ID:</span>
                  <span className="font-mono text-sm text-gray-900 dark:text-white">{contest._id || contest.id}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600 dark:text-gray-400">Series ID:</span>
                  <span className="font-mono text-sm text-gray-900 dark:text-white">{contest.seriesId}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600 dark:text-gray-400">Status:</span>
                  <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold ${
                    contest.isActive
                      ? 'bg-green-100 text-green-800 dark:bg-green-500/20 dark:text-green-400'
                      : 'bg-gray-100 text-gray-800 dark:bg-gray-500/20 dark:text-gray-400'
                  }`}>
                    {contest.isActive ? <FaCheck className="mr-1" /> : <FaTimes className="mr-1" />}
                    {contest.isActive ? 'Active' : 'Inactive'}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600 dark:text-gray-400">Visibility:</span>
                  <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold ${
                    contest.isPrivate
                      ? 'bg-purple-100 text-purple-800 dark:bg-purple-500/20 dark:text-purple-400'
                      : 'bg-blue-100 text-blue-800 dark:bg-blue-500/20 dark:text-blue-400'
                  }`}>
                    {contest.isPrivate ? <FaLock className="mr-1" /> : <FaUnlock className="mr-1" />}
                    {contest.isPrivate ? 'Private' : 'Public'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600 dark:text-gray-400">Created:</span>
                  <span className="text-sm text-gray-900 dark:text-white">{formatDate(contest.createdAt)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600 dark:text-gray-400">Updated:</span>
                  <span className="text-sm text-gray-900 dark:text-white">{formatDate(contest.updatedAt)}</span>
                </div>
              </div>
            </div>

            {/* Plan Selection Card */}
            <div className="bg-gray-50 dark:bg-dark-300/40 rounded-lg p-6 border border-gray-200 dark:border-dark-300/40">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                <FaCrown className="text-yellow-500" />
                Plan Selection
              </h3>
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-gray-600 dark:text-gray-400">Tier:</span>
                  <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-semibold capitalize ${
                    contest.planSelection?.tier === 'starter'
                      ? 'bg-gray-100 text-gray-800 dark:bg-gray-500/20 dark:text-gray-400'
                      : contest.planSelection?.tier === 'pro'
                      ? 'bg-blue-100 text-blue-800 dark:bg-blue-500/20 dark:text-blue-400'
                      : 'bg-purple-100 text-purple-800 dark:bg-purple-500/20 dark:text-purple-400'
                  }`}>
                    {contest.planSelection?.tier || 'None'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600 dark:text-gray-400">Add-ons:</span>
                  <span className="text-gray-900 dark:text-white">
                    {contest.planSelection?.addOns?.length > 0
                      ? contest.planSelection.addOns.join(', ')
                      : 'None'}
                  </span>
                </div>
              </div>
            </div>

            {/* Owner Info Card */}
            <div className="bg-gray-50 dark:bg-dark-300/40 rounded-lg p-6 border border-gray-200 dark:border-dark-300/40">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                <FaUserTie className="text-green-500" />
                Owner Information
              </h3>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-gray-600 dark:text-gray-400">Name:</span>
                  <span className="text-gray-900 dark:text-white capitalize">
                    {ownerInfo.firstname} {ownerInfo.lastname}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600 dark:text-gray-400">Email:</span>
                  <span className="text-gray-900 dark:text-white">{ownerInfo.email || 'N/A'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600 dark:text-gray-400">User ID:</span>
                  <span className="font-mono text-xs text-gray-900 dark:text-white">{ownerInfo.userId || contest.ownerId}</span>
                </div>
              </div>
            </div>

            {/* Lock & Visibility Card */}
            <div className="bg-gray-50 dark:bg-dark-300/40 rounded-lg p-6 border border-gray-200 dark:border-dark-300/40">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                <FaLock className="text-red-500" />
                Lock & Visibility
              </h3>
              <div className="grid grid-cols-3 gap-4">
                <div className={`text-center p-4 rounded-lg border ${
                  contest.lockStatus?.isTeamChangesLocked
                    ? 'bg-red-50 dark:bg-red-500/10 border-red-200 dark:border-red-500/30'
                    : 'bg-green-50 dark:bg-green-500/10 border-green-200 dark:border-green-500/30'
                }`}>
                  <div className="flex justify-center mb-2">
                    {contest.lockStatus?.isTeamChangesLocked
                      ? <FaLock className="h-6 w-6 text-red-500" />
                      : <FaUnlock className="h-6 w-6 text-green-500" />
                    }
                  </div>
                  <div className={`text-sm font-bold ${contest.lockStatus?.isTeamChangesLocked ? 'text-red-700 dark:text-red-400' : 'text-green-700 dark:text-green-400'}`}>
                    {contest.lockStatus?.isTeamChangesLocked ? 'Locked' : 'Unlocked'}
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">Team Changes</div>
                </div>
                <div className={`text-center p-4 rounded-lg border ${
                  contest.lockStatus?.isRulesLocked
                    ? 'bg-red-50 dark:bg-red-500/10 border-red-200 dark:border-red-500/30'
                    : 'bg-green-50 dark:bg-green-500/10 border-green-200 dark:border-green-500/30'
                }`}>
                  <div className="flex justify-center mb-2">
                    {contest.lockStatus?.isRulesLocked
                      ? <FaLock className="h-6 w-6 text-red-500" />
                      : <FaUnlock className="h-6 w-6 text-green-500" />
                    }
                  </div>
                  <div className={`text-sm font-bold ${contest.lockStatus?.isRulesLocked ? 'text-red-700 dark:text-red-400' : 'text-green-700 dark:text-green-400'}`}>
                    {contest.lockStatus?.isRulesLocked ? 'Locked' : 'Unlocked'}
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">Rules</div>
                </div>
                <div className={`text-center p-4 rounded-lg border ${
                  contest.teamsVisibility
                    ? 'bg-green-50 dark:bg-green-500/10 border-green-200 dark:border-green-500/30'
                    : 'bg-gray-50 dark:bg-gray-500/10 border-gray-200 dark:border-gray-500/30'
                }`}>
                  <div className="flex justify-center mb-2">
                    {contest.teamsVisibility
                      ? <FaEye className="h-6 w-6 text-green-500" />
                      : <FaTimes className="h-6 w-6 text-gray-400" />
                    }
                  </div>
                  <div className={`text-sm font-bold ${contest.teamsVisibility ? 'text-green-700 dark:text-green-400' : 'text-gray-600 dark:text-gray-400'}`}>
                    {contest.teamsVisibility ? 'Visible' : 'Hidden'}
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">Teams Visibility</div>
                </div>
              </div>
            </div>

            {/* Member Stats Card */}
            <div className="bg-gray-50 dark:bg-dark-300/40 rounded-lg p-6 border border-gray-200 dark:border-dark-300/40">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                <FaUsers className="text-indigo-500" />
                Member Statistics
              </h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="text-center p-4 bg-white dark:bg-dark-400/60 rounded-lg">
                  <div className="text-3xl font-bold text-blue-600 dark:text-blue-400">{contest.totalMembers || 0}</div>
                  <div className="text-sm text-gray-600 dark:text-gray-400">Total Members</div>
                </div>
                <div className="text-center p-4 bg-white dark:bg-dark-400/60 rounded-lg">
                  <div className="text-3xl font-bold text-green-600 dark:text-green-400">{contest.acceptedMembers || 0}</div>
                  <div className="text-sm text-gray-600 dark:text-gray-400">Accepted</div>
                </div>
                <div className="text-center p-4 bg-white dark:bg-dark-400/60 rounded-lg">
                  <div className="text-3xl font-bold text-yellow-600 dark:text-yellow-400">{contest.pendingMembers || 0}</div>
                  <div className="text-sm text-gray-600 dark:text-gray-400">Pending</div>
                </div>
                <div className="text-center p-4 bg-white dark:bg-dark-400/60 rounded-lg">
                  <div className="text-3xl font-bold text-purple-600 dark:text-purple-400">{contest.registeredTeamCount || 0}</div>
                  <div className="text-sm text-gray-600 dark:text-gray-400">Teams</div>
                </div>
              </div>
            </div>

            {/* Match List Card — only for match_clash */}
            {isMatchClash && seriesMatches.length > 0 && (
              <div className="bg-gray-50 dark:bg-dark-300/40 rounded-lg p-6 border border-gray-200 dark:border-dark-300/40 lg:col-span-2">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                  <FaList className="text-amber-500" />
                  Match Schedule
                  <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-semibold bg-amber-100 dark:bg-amber-500/20 text-amber-800 dark:text-amber-400">
                    {seriesMatches.length} matches
                  </span>
                </h3>
                <div className="space-y-2">
                  {seriesMatches.map((m: any) => {
                    const teamsForMatch = fantasyTeams.filter((ft: any) => ft.matchId === m.matchId).length;
                    return (
                      <div key={m.matchId} className={`flex items-center justify-between px-4 py-3 rounded-lg border ${
                        m.state === 'Complete' ? 'bg-green-50 dark:bg-green-500/5 border-green-200 dark:border-green-500/20' :
                        m.state === 'Live' || m.state === 'In Progress' ? 'bg-blue-50 dark:bg-blue-500/5 border-blue-200 dark:border-blue-500/20' :
                        'bg-white dark:bg-dark-400/40 border-gray-200 dark:border-dark-300/40'
                      }`}>
                        <div className="flex items-center gap-3">
                          <span className="text-xs font-bold text-gray-500 dark:text-gray-400 w-8">M{m.matchOrderId || '?'}</span>
                          <span className="text-sm font-semibold text-gray-900 dark:text-white">
                            {m.localTeam?.teamSName || '?'} vs {m.visitorTeam?.teamSName || '?'}
                          </span>
                          <span className="text-xs text-gray-500 dark:text-gray-400">
                            {m.startDate ? new Date(m.startDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : ''}
                          </span>
                        </div>
                        <div className="flex items-center gap-3">
                          {teamsForMatch > 0 && (
                            <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-semibold bg-blue-100 dark:bg-blue-500/20 text-blue-800 dark:text-blue-400">
                              {teamsForMatch} teams
                            </span>
                          )}
                          <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-semibold ${
                            m.state === 'Complete' ? 'bg-green-100 dark:bg-green-500/20 text-green-800 dark:text-green-400' :
                            m.state === 'Live' || m.state === 'In Progress' ? 'bg-blue-100 dark:bg-blue-500/20 text-blue-800 dark:text-blue-400' :
                            'bg-gray-100 dark:bg-gray-500/20 text-gray-600 dark:text-gray-400'
                          }`}>
                            {m.state || 'Upcoming'}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          {/* Rules Config Tab */}
          <div className="space-y-6">
            {contest.rulesConfig?.map((section: any, sectionIndex: number) => (
              <div key={sectionIndex} className="bg-gray-50 dark:bg-dark-300/40 rounded-lg border border-gray-200 dark:border-dark-300/40 overflow-hidden">
                <div className={`px-6 py-4 border-b border-gray-200 dark:border-dark-300/40 flex items-center justify-between ${
                  section.locked ? 'bg-gray-100 dark:bg-dark-400/40' : 'bg-white dark:bg-dark-400/60'
                }`}>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                    <FaList className="text-blue-500" />
                    {section.section}
                  </h3>
                  {section.locked && (
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold bg-red-100 text-red-800 dark:bg-red-500/20 dark:text-red-400">
                      <FaLock className="mr-1 h-3 w-3" />
                      Locked
                    </span>
                  )}
                </div>
                <div className="p-6">
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200 dark:divide-dark-300/40">
                      <thead className="bg-gray-100 dark:bg-dark-400/40">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Field</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Value</th>
                          <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Default</th>
                          <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Status</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200 dark:divide-dark-300/40">
                        {section.fields?.map((field: any, fieldIndex: number) => (
                          <tr key={fieldIndex} className={field.disabled ? 'opacity-50' : ''}>
                            <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-white">
                              {field.label}
                              {field.required && <span className="text-red-500 ml-1">*</span>}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">
                              {field.type === 'minMaxRange' ? (
                                <span className="font-mono">
                                  {field.value?.min ?? 0} - {field.value?.max ?? 0}
                                </span>
                              ) : field.type === 'boolean' ? (
                                <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold ${
                                  field.value ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'
                                }`}>
                                  {field.value ? 'Yes' : 'No'}
                                </span>
                              ) : (
                                <span className="font-semibold">{String(field.value ?? 'N/A')}</span>
                              )}
                            </td>
                            <td className="px-4 py-3 text-center text-sm text-gray-500 dark:text-gray-400">
                              {field.type === 'minMaxRange' ? (
                                <span className="font-mono text-xs">
                                  {field.default?.min ?? 0} - {field.default?.max ?? 0}
                                </span>
                              ) : (
                                String(field.default ?? 'N/A')
                              )}
                            </td>
                            <td className="px-4 py-3 text-center">
                              <div className="flex items-center justify-center gap-1">
                                {field.locked && (
                                  <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs bg-red-100 text-red-600 dark:bg-red-500/20 dark:text-red-400">
                                    <FaLock className="h-2.5 w-2.5" />
                                  </span>
                                )}
                                {field.disabled && (
                                  <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs bg-gray-100 text-gray-600 dark:bg-gray-500/20 dark:text-gray-400">
                                    Disabled
                                  </span>
                                )}
                                {!field.locked && !field.disabled && (
                                  <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs bg-green-100 text-green-600 dark:bg-green-500/20 dark:text-green-400">
                                    Active
                                  </span>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </TabPanel>

        <TabPanel value={tabValue} index={2}>
          {/* Scoring Config Tab */}
          <div className="space-y-6">
            {contest.scoringSystemConfig?.map((section: any, sectionIndex: number) => (
              <div key={sectionIndex} className="bg-gray-50 dark:bg-dark-300/40 rounded-lg border border-gray-200 dark:border-dark-300/40 overflow-hidden">
                <div className="px-6 py-4 border-b border-gray-200 dark:border-dark-300/40 bg-white dark:bg-dark-400/60">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                    <FaTrophy className="text-yellow-500" />
                    {section.section}
                  </h3>
                </div>
                <div className="p-6">
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200 dark:divide-dark-300/40">
                      <thead className="bg-gray-100 dark:bg-dark-400/40">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Scoring Rule</th>
                          <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Points</th>
                          <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Operation</th>
                          <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Default</th>
                          <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Status</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200 dark:divide-dark-300/40">
                        {section.fields?.map((field: any, fieldIndex: number) => (
                          <tr key={fieldIndex} className={!field.active ? 'opacity-50' : ''}>
                            <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-white">
                              {field.label}
                              {field.required && <span className="text-red-500 ml-1">*</span>}
                            </td>
                            <td className="px-4 py-3 text-center">
                              {field.type === 'boolean' ? (
                                <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold ${
                                  field.value ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'
                                }`}>
                                  {field.value ? 'Yes' : 'No'}
                                </span>
                              ) : (field.type === 'strikeRate' || field.type === 'economyRate') ? (
                                <span className="inline-flex flex-col items-center text-xs text-gray-700 dark:text-gray-300">
                                  <span className="font-medium">Min {field.type === 'strikeRate' ? 'Balls' : 'Overs'}: {(field.value as any)?.minBalls ?? (field.value as any)?.minOvers ?? '—'}</span>
                                  {(field.value as any)?.ranges?.map((r: any, i: number) => (
                                    <span key={i}>{r.min ?? '—'}–{r.max ?? '—'}: <b>{r.points > 0 ? '+' : ''}{r.points}</b></span>
                                  ))}
                                </span>
                              ) : (
                                <span className={`inline-flex items-center px-3 py-1 rounded-lg text-sm font-bold ${
                                  field.operation === 'substract'
                                    ? 'bg-red-100 text-red-700 dark:bg-red-500/20 dark:text-red-400'
                                    : field.operation === 'multiplier'
                                    ? 'bg-purple-100 text-purple-700 dark:bg-purple-500/20 dark:text-purple-400'
                                    : 'bg-green-100 text-green-700 dark:bg-green-500/20 dark:text-green-400'
                                }`}>
                                  {field.operation === 'substract' && '-'}
                                  {field.operation === 'multiplier' && 'x'}
                                  {field.operation === 'addition' && '+'}
                                  {typeof field.value === 'object' && field.value !== null ? JSON.stringify(field.value) : field.value}
                                </span>
                              )}
                            </td>
                            <td className="px-4 py-3 text-center text-sm text-gray-500 dark:text-gray-400 capitalize">
                              {field.operation || 'N/A'}
                            </td>
                            <td className="px-4 py-3 text-center text-sm text-gray-500 dark:text-gray-400">
                              {typeof field.default === 'object' && field.default !== null ? JSON.stringify(field.default) : String(field.default ?? 'N/A')}
                            </td>
                            <td className="px-4 py-3 text-center">
                              <div className="flex items-center justify-center gap-1">
                                {field.locked && (
                                  <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs bg-red-100 text-red-600 dark:bg-red-500/20 dark:text-red-400">
                                    <FaLock className="h-2.5 w-2.5" />
                                  </span>
                                )}
                                {!field.active && (
                                  <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs bg-gray-100 text-gray-600 dark:bg-gray-500/20 dark:text-gray-400">
                                    Inactive
                                  </span>
                                )}
                                {field.active && !field.locked && (
                                  <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs bg-green-100 text-green-600 dark:bg-green-500/20 dark:text-green-400">
                                    Active
                                  </span>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {section.notes && section.notes.length > 0 && (
                    <div className="mt-4 p-4 bg-yellow-50 dark:bg-yellow-500/10 border border-yellow-200 dark:border-yellow-500/30 rounded-lg">
                      <p className="text-sm text-yellow-800 dark:text-yellow-400">
                        <strong>Note:</strong> {section.notes.join(' ')}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </TabPanel>

        <TabPanel value={tabValue} index={3}>
          {/* Teams Tab */}
          {/* Contest owner banner — name + userId at the top so admins
              can quickly identify the contest's creator without
              jumping to the Overview tab. */}
          <div className="mb-4 px-4 py-3 rounded-lg bg-blue-50 dark:bg-primary-500/10 border border-blue-200 dark:border-primary-500/30 flex flex-wrap items-center gap-3">
            <span className="text-xs font-semibold uppercase tracking-wider text-blue-800 dark:text-primary-400">Contest Owner</span>
            <span className="text-sm font-semibold text-gray-900 dark:text-white">
              {`${ownerInfo.firstname || ''} ${ownerInfo.lastname || ''}`.trim() || ownerInfo.email || '—'}
            </span>
            <span className="text-xs font-mono text-gray-600 dark:text-gray-400">{ownerInfo.userId || (contest as any)?.ownerId || '—'}</span>
          </div>
          {loadingFantasyTeams ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              <span className="ml-3 text-gray-600 dark:text-gray-400">Loading fantasy teams...</span>
            </div>
          ) : fantasyTeams.length > 0 ? (
            <div className="space-y-3">
              <div className="mb-4 flex items-center gap-3 flex-wrap">
                <span className="inline-flex px-3 py-1 rounded-full text-xs font-semibold bg-blue-100 dark:bg-primary-500/20 text-blue-800 dark:text-primary-400 border border-blue-300 dark:border-primary-500/30">
                  {isMatchClash && selectedMatchFilter !== 'all'
                    ? `${fantasyTeams.filter((ft: any) => String(ft.matchId) === selectedMatchFilter).length} Teams for Match`
                    : `${fantasyTeams.length} Fantasy Teams`
                  }
                </span>
                {isMatchClash && seriesMatches.length > 0 && (
                  <select
                    value={selectedMatchFilter}
                    onChange={(e) => setSelectedMatchFilter(e.target.value)}
                    className="px-3 py-1.5 border border-gray-300 dark:border-dark-300/40 rounded-lg bg-white dark:bg-dark-400/60 text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-amber-500"
                  >
                    <option value="all">All Matches</option>
                    {seriesMatches.map((m: any) => (
                      <option key={m.matchId} value={String(m.matchId)}>
                        M{m.matchOrderId || '?'}: {m.localTeam?.teamSName || '?'} vs {m.visitorTeam?.teamSName || '?'}
                        {m.state === 'Complete' ? ' ✓' : m.state === 'Live' ? ' 🔴' : ''}
                      </option>
                    ))}
                  </select>
                )}
              </div>
              {(() => {
                // Resolve team owner display name from contestMembers
                // (loaded on Members tab) or from the contest owner —
                // covers the case where the owner created teams. Falls
                // back to '—' if neither source has the user yet.
                const memberByUserId = new Map<string, any>();
                for (const m of contestMembers || []) memberByUserId.set(String(m.userId), m);
                const ownerUid = String(ownerInfo.userId || (contest as any)?.ownerId || '');
                const resolveOwnerName = (uid: string) => {
                  if (!uid) return '—';
                  if (uid === ownerUid && (ownerInfo.firstname || ownerInfo.lastname || ownerInfo.email)) {
                    return `${ownerInfo.firstname || ''} ${ownerInfo.lastname || ''}`.trim() || ownerInfo.email || '—';
                  }
                  const m = memberByUserId.get(uid);
                  if (!m) return '—';
                  return `${m.firstname || m.firstName || ''} ${m.lastname || m.lastName || ''}`.trim() || m.email || '—';
                };
                return (isMatchClash && selectedMatchFilter !== 'all'
                  ? fantasyTeams.filter((ft: any) => String(ft.matchId) === selectedMatchFilter)
                  : fantasyTeams
                ).map((ft: any, index: number) => {
                const teamKey = ft._id || ft.id || String(index);
                const isExpanded = expandedTeams.has(teamKey);
                const matchInfo = isMatchClash && ft.matchId ? seriesMatches.find((m: any) => m.matchId === ft.matchId) : null;
                const players = ft.players || [];
                const ownerDisplay = resolveOwnerName(String(ft.userId || ''));

                return (
                  <div key={teamKey} className="bg-gray-50 dark:bg-dark-300/40 rounded-lg border border-gray-200 dark:border-dark-300/40 overflow-hidden">
                    <button
                      onClick={() => toggleTeamExpand(teamKey)}
                      className="w-full flex items-center justify-between px-6 py-4 hover:bg-gray-100 dark:hover:bg-dark-400/50 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        {isExpanded ? (
                          <FaChevronDown className="h-4 w-4 text-gray-500 dark:text-gray-400" />
                        ) : (
                          <FaChevronRight className="h-4 w-4 text-gray-500 dark:text-gray-400" />
                        )}
                        <span className="text-base font-semibold text-gray-900 dark:text-white">{ft.fantasyName || 'Unnamed Team'}</span>
                        {matchInfo && selectedMatchFilter === 'all' && (
                          <span className="inline-flex px-2.5 py-1 rounded-full text-xs font-semibold bg-amber-100 dark:bg-amber-500/20 text-amber-800 dark:text-amber-400 border border-amber-300 dark:border-amber-500/30">
                            M{matchInfo.matchOrderId}: {matchInfo.localTeam?.teamSName || '?'} vs {matchInfo.visitorTeam?.teamSName || '?'}
                          </span>
                        )}
                        <span className="inline-flex px-2.5 py-1 rounded-full text-xs font-semibold bg-blue-100 dark:bg-primary-500/20 text-blue-800 dark:text-primary-400 border border-blue-300 dark:border-primary-500/30">
                          {players.length} players
                        </span>
                        {ft.totalPoints != null && (
                          <span className="inline-flex px-2.5 py-1 rounded-full text-xs font-bold bg-green-100 dark:bg-green-500/20 text-green-800 dark:text-green-400 border border-green-300 dark:border-green-500/30">
                            {ft.totalPoints} pts
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-3" onClick={e => e.stopPropagation()}>
                        <div className="flex flex-col items-end">
                          <span className="text-xs font-semibold text-gray-700 dark:text-gray-300">{ownerDisplay}</span>
                          <span className="text-[10px] text-gray-500 dark:text-gray-400 font-mono">{ft.userId || 'N/A'}</span>
                        </div>
                        <button
                          onClick={(e) => { e.stopPropagation(); setReassignTeam(ft); setReassignUserId(''); }}
                          className="text-[10px] font-semibold px-2 py-0.5 rounded bg-amber-100 dark:bg-amber-500/20 text-amber-700 dark:text-amber-400 border border-amber-300 dark:border-amber-500/30 hover:bg-amber-200 dark:hover:bg-amber-500/30 transition-colors"
                        >
                          Reassign
                        </button>
                      </div>
                    </button>
                    {isExpanded && (
                      <div className="border-t border-gray-200 dark:border-dark-300/40">
                        {/* Captain & Vice-Captain info */}
                        {(ft.captainPlayerId || ft.viceCaptainPlayerId) && (
                          <div className="px-6 py-3 bg-yellow-50 dark:bg-yellow-500/10 border-b border-gray-200 dark:border-dark-300/40 flex gap-4 text-sm">
                            {ft.captainPlayerId && (
                              <span className="flex items-center gap-1">
                                <FaCrown className="text-yellow-500 h-3.5 w-3.5" />
                                <span className="text-gray-600 dark:text-gray-400">Captain:</span>
                                <span className="font-semibold text-gray-900 dark:text-white">
                                  {playerTeamMap.get(String(ft.captainPlayerId))?.playerName || ft.captainPlayerId}
                                </span>
                              </span>
                            )}
                            {ft.viceCaptainPlayerId && (
                              <span className="flex items-center gap-1">
                                <FaCrown className="text-gray-400 h-3.5 w-3.5" />
                                <span className="text-gray-600 dark:text-gray-400">Vice-Captain:</span>
                                <span className="font-semibold text-gray-900 dark:text-white">
                                  {playerTeamMap.get(String(ft.viceCaptainPlayerId))?.playerName || ft.viceCaptainPlayerId}
                                </span>
                              </span>
                            )}
                          </div>
                        )}
                        {players.length > 0 ? (
                          <table className="min-w-full divide-y divide-gray-200 dark:divide-dark-300/40">
                            <thead className="bg-gray-100 dark:bg-dark-400/30">
                              <tr>
                                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">Image</th>
                                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">Player ID</th>
                                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">Player Name</th>
                                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">Team</th>
                                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">Role</th>
                                <th className="px-6 py-3 text-right text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">Points</th>
                              </tr>
                            </thead>
                            <tbody className="bg-white dark:bg-transparent divide-y divide-gray-200 dark:divide-dark-300/40">
                              {players.map((player: any, pIndex: number) => {
                                const playerId = typeof player === 'number' || typeof player === 'string' ? player : (player.playerId || player.id);
                                const squadInfo = playerTeamMap.get(String(playerId));
                                const isCaptain = String(playerId) === String(ft.captainPlayerId);
                                const isViceCaptain = String(playerId) === String(ft.viceCaptainPlayerId);
                                const fpEntry = Array.isArray(ft.fantasyPoints) ? ft.fantasyPoints.find((fp: any) => fp.playerId === Number(playerId)) : null;
                                const playerPoints = fpEntry?.score ?? ft.fantasyPoints?.[playerId] ?? null;
                                const displayName = (typeof player === 'object' ? player.playerName : null) || squadInfo?.playerName || '-';
                                const displayRole = (typeof player === 'object' ? (player.playerRole || player.role) : null) || squadInfo?.role || '-';
                                const displayTeam = squadInfo?.teamShortName || squadInfo?.teamName || '-';

                                return (
                                  <tr key={playerId || pIndex} className="hover:bg-gray-50 dark:hover:bg-dark-400/50 transition-colors">
                                    <td className="px-6 py-3 whitespace-nowrap">
                                      <img
                                        src={`https://cricclash-s3.s3.us-east-2.amazonaws.com/players/${playerId}.jpg`}
                                        alt={displayName}
                                        className="w-8 h-8 rounded-full object-cover border border-gray-200 dark:border-dark-300/40"
                                        onError={(e) => {
                                          const target = e.target as HTMLImageElement;
                                          target.onerror = null;
                                          target.src = 'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSI0MCIgaGVpZ2h0PSI0MCIgdmlld0JveD0iMCAwIDI0IDI0IiBmaWxsPSJub25lIiBzdHJva2U9IiM5Y2EzYWYiIHN0cm9rZS13aWR0aD0iMiIgc3Ryb2tlLWxpbmVjYXA9InJvdW5kIiBzdHJva2UtbGluZWpvaW49InJvdW5kIj48cGF0aCBkPSJNMjAgMjF2LTJhNCA0IDAgMCAwLTQtNEg4YTQgNCAwIDAgMC00IDR2MiI+PC9wYXRoPjxjaXJjbGUgY3g9IjEyIiBjeT0iNyIgcj0iNCI+PC9jaXJjbGU+PC9zdmc+';
                                        }}
                                      />
                                    </td>
                                    <td className="px-6 py-3 whitespace-nowrap text-sm text-gray-900 dark:text-gray-300">
                                      <div className="flex items-center gap-2">
                                        {playerId}
                                        {isCaptain && (
                                          <span className="inline-flex px-1.5 py-0.5 rounded text-xs font-bold bg-yellow-100 dark:bg-yellow-500/20 text-yellow-800 dark:text-yellow-400 border border-yellow-300 dark:border-yellow-500/30">C</span>
                                        )}
                                        {isViceCaptain && (
                                          <span className="inline-flex px-1.5 py-0.5 rounded text-xs font-bold bg-gray-200 dark:bg-gray-500/20 text-gray-700 dark:text-gray-400 border border-gray-300 dark:border-gray-500/30">VC</span>
                                        )}
                                      </div>
                                    </td>
                                    <td className="px-6 py-3 whitespace-nowrap text-sm font-semibold text-gray-900 dark:text-white">{displayName}</td>
                                    <td className="px-6 py-3 whitespace-nowrap text-sm text-gray-900 dark:text-gray-300">{displayTeam}</td>
                                    <td className="px-6 py-3 whitespace-nowrap text-sm text-gray-900 dark:text-gray-300">{displayRole}</td>
                                    <td className="px-6 py-3 whitespace-nowrap text-sm text-right font-bold text-blue-600 dark:text-blue-400">{playerPoints ?? '-'}</td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        ) : (
                          <div className="px-6 py-4 text-sm text-gray-500 dark:text-gray-400">No players in this team</div>
                        )}
                      </div>
                    )}
                  </div>
                );
              });
              })()}
            </div>
          ) : (
            <div className="text-center py-12">
              <FaUsers className="mx-auto h-12 w-12 text-gray-400 mb-4" />
              <p className="text-gray-600 dark:text-gray-400">No fantasy teams found</p>
            </div>
          )}
        </TabPanel>

        <TabPanel value={tabValue} index={4}>
          {/* Members Tab */}
          {loadingMembers ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              <span className="ml-3 text-gray-600 dark:text-gray-400">Loading members...</span>
            </div>
          ) : contestMembers.length > 0 ? (
            <div className="ag-theme-material" style={{ height: 'calc(100vh - 350px)', minHeight: '400px', width: '100%' }}>
              <AgGridReact
                rowData={contestMembers}
                columnDefs={memberColumns}
                defaultColDef={{ sortable: true, resizable: true, filter: true }}
                pagination
                paginationPageSize={20}
                theme={themeQuartz}
              />
            </div>
          ) : (
            <div className="text-center py-12">
              <FaUsers className="mx-auto h-12 w-12 text-gray-400 mb-4" />
              <p className="text-gray-600 dark:text-gray-400">No members found</p>
            </div>
          )}
        </TabPanel>

        <TabPanel value={tabValue} index={5}>
          {/* Leaderboard Tab */}
          {isMatchClash && seriesMatches.length > 0 && (
            <div className="mb-4 flex items-center gap-3 flex-wrap">
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">View by match:</span>
              <select
                value={selectedMatchFilter}
                onChange={(e) => setSelectedMatchFilter(e.target.value)}
                className="px-3 py-1.5 border border-gray-300 dark:border-dark-300/40 rounded-lg bg-white dark:bg-dark-400/60 text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-amber-500"
              >
                <option value="all">Overall Leaderboard</option>
                {seriesMatches.map((m: any) => (
                  <option key={m.matchId} value={String(m.matchId)}>
                    M{m.matchOrderId || '?'}: {m.localTeam?.teamSName || '?'} vs {m.visitorTeam?.teamSName || '?'}
                    {m.state === 'Complete' ? ' ✓' : m.state === 'Live' ? ' 🔴' : ''}
                  </option>
                ))}
              </select>
              {selectedMatchFilter !== 'all' && (
                <span className="inline-flex px-2.5 py-1 rounded-full text-xs font-semibold bg-amber-100 dark:bg-amber-500/20 text-amber-800 dark:text-amber-400 border border-amber-300 dark:border-amber-500/30">
                  Match {seriesMatches.find((m: any) => String(m.matchId) === selectedMatchFilter)?.matchOrderId || '?'}
                </span>
              )}
            </div>
          )}
          {(isMatchClash && selectedMatchFilter !== 'all') ? (
            /* Per-match leaderboard */
            loadingMatchLeaderboard ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-600"></div>
                <span className="ml-3 text-gray-600 dark:text-gray-400">Loading match leaderboard...</span>
              </div>
            ) : matchLeaderboard.length > 0 ? (
              <div className="ag-theme-material" style={{ height: 'calc(100vh - 350px)', minHeight: '400px', width: '100%' }}>
                <AgGridReact
                  rowData={matchLeaderboard}
                  columnDefs={leaderboardColumns}
                  defaultColDef={{ sortable: true, resizable: true, filter: true }}
                  pagination
                  paginationPageSize={20}
                  theme={themeQuartz}
                />
              </div>
            ) : (
              <div className="text-center py-12">
                <FaTrophy className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                <p className="text-gray-600 dark:text-gray-400">No leaderboard data for this match</p>
              </div>
            )
          ) : (
            /* Overall leaderboard */
            loadingLeaderboard ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                <span className="ml-3 text-gray-600 dark:text-gray-400">Loading leaderboard...</span>
              </div>
            ) : contestLeaderboard.length > 0 ? (
              <div className="ag-theme-material" style={{ height: 'calc(100vh - 350px)', minHeight: '400px', width: '100%' }}>
                <AgGridReact
                  rowData={contestLeaderboard}
                  columnDefs={leaderboardColumns}
                  defaultColDef={{ sortable: true, resizable: true, filter: true }}
                  pagination
                  paginationPageSize={20}
                  theme={themeQuartz}
                />
              </div>
            ) : (
              <div className="text-center py-12">
                <FaTrophy className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                <p className="text-gray-600 dark:text-gray-400">No leaderboard data available</p>
              </div>
            )
          )}
        </TabPanel>

        <TabPanel value={tabValue} index={6}>
          {/* Player Scores Tab */}
          {loadingPlayerScores ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              <span className="ml-3 text-gray-600 dark:text-gray-400">Loading player scores...</span>
            </div>
          ) : (
            <>
              {/* Team Filter */}
              {seriesTeams.length > 0 && playerScores.length > 0 && (
                <div className="mb-6 flex justify-between items-center">
                  <div className="min-w-[250px] max-w-[350px]">
                    <select
                      value={selectedPlayerScoreTeamFilter === null ? '' : selectedPlayerScoreTeamFilter}
                      onChange={(e) => setSelectedPlayerScoreTeamFilter(e.target.value === '' ? null : Number(e.target.value))}
                      className="block w-full px-3 py-2 border border-gray-300 dark:border-dark-300/40 rounded-lg bg-white dark:bg-dark-400/60 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                    >
                      <option value="">All Teams</option>
                      {seriesTeams.map((team: any) => (
                        <option key={team.teamId} value={team.teamId}>
                          {team.teamName}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="flex gap-2 items-center">
                    <span className="inline-flex px-3 py-1 rounded-full text-xs font-semibold bg-blue-100 dark:bg-primary-500/20 text-blue-800 dark:text-primary-400 border border-blue-300 dark:border-primary-500/30">
                      Total Players: {flattenedPlayerScores.length}
                    </span>
                    {selectedPlayerScoreTeamFilter !== null && (
                      <span className="inline-flex px-3 py-1 rounded-full text-xs font-semibold bg-sky-100 dark:bg-sky-500/20 text-sky-800 dark:text-sky-400 border border-sky-300 dark:border-sky-500/30">
                        Viewing: {seriesTeams.find((t: any) => t.teamId === selectedPlayerScoreTeamFilter)?.teamName || 'Unknown Team'}
                      </span>
                    )}
                  </div>
                </div>
              )}

              {flattenedPlayerScores.length > 0 ? (
                <div className="ag-theme-material" style={{ height: 'calc(100vh - 400px)', minHeight: '400px', width: '100%' }}>
                  <AgGridReact
                    rowData={flattenedPlayerScores}
                    columnDefs={playerScoresColumns}
                    defaultColDef={{ sortable: true, resizable: true }}
                    pagination={true}
                    paginationPageSize={25}
                    paginationPageSizeSelector={[10, 25, 50, 100]}
                    rowHeight={50}
                    theme={themeQuartz}
                    getRowId={(params: any) => params.data._id || params.data.playerId || `player-${params.data.playerName}`}
                    onRowClicked={handlePlayerScoreRowClick}
                    rowClass="cursor-pointer hover:bg-blue-50"
                  />
                </div>
              ) : (
                <div className="text-center py-12">
                  <FaChartBar className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                  <p className="text-gray-600 dark:text-gray-400">
                    {playerScores.length === 0 ? 'No player scores available' : 'No players found for the selected team'}
                  </p>
                </div>
              )}
            </>
          )}
        </TabPanel>

        <TabPanel value={tabValue} index={7}>
          {/* Fantasy Points Tab */}
          {loadingFantasyPoints ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              <span className="ml-3 text-gray-600 dark:text-gray-400">Loading fantasy points...</span>
            </div>
          ) : (
            <>
              {/* Squad Filter */}
              {fpSquads.length > 0 && fantasyPointsBreakdown.length > 0 && (
                <div className="mb-6 flex justify-between items-center">
                  <div className="min-w-[250px] max-w-[350px]">
                    <select
                      value={selectedFPSquadFilter}
                      onChange={(e) => setSelectedFPSquadFilter(e.target.value)}
                      className="block w-full px-3 py-2 border border-gray-300 dark:border-dark-300/40 rounded-lg bg-white dark:bg-dark-400/60 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                    >
                      <option value="">All Squads</option>
                      {fpSquads.map((squad) => (
                        <option key={squad.id} value={squad.id}>
                          {squad.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="flex gap-2 items-center">
                    <span className="inline-flex px-3 py-1 rounded-full text-xs font-semibold bg-blue-100 dark:bg-primary-500/20 text-blue-800 dark:text-primary-400 border border-blue-300 dark:border-primary-500/30">
                      Total Players: {filteredFantasyPoints.length}
                    </span>
                    {selectedFPSquadFilter && (
                      <span className="inline-flex px-3 py-1 rounded-full text-xs font-semibold bg-sky-100 dark:bg-sky-500/20 text-sky-800 dark:text-sky-400 border border-sky-300 dark:border-sky-500/30">
                        Viewing: {fpSquads.find((s) => s.id === selectedFPSquadFilter)?.name || 'Unknown Squad'}
                      </span>
                    )}
                  </div>
                </div>
              )}

              {filteredFantasyPoints.length > 0 ? (
                <div className="ag-theme-material" style={{ height: 'calc(100vh - 400px)', minHeight: '400px', width: '100%' }}>
                  <AgGridReact
                    rowData={filteredFantasyPoints}
                    columnDefs={fantasyPointsColumns}
                    defaultColDef={{ sortable: true, resizable: true }}
                    pagination={true}
                    paginationPageSize={25}
                    paginationPageSizeSelector={[10, 25, 50, 100]}
                    rowHeight={50}
                    theme={themeQuartz}
                    getRowId={(params: any) => params.data._id || String(params.data.playerId) || `fp-${params.data.playerName}`}
                    onRowClicked={handleFPRowClick}
                    rowClass="cursor-pointer hover:bg-blue-50"
                  />
                </div>
              ) : (
                <div className="text-center py-12">
                  <FaChartBar className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                  <p className="text-gray-600 dark:text-gray-400">
                    {fantasyPointsBreakdown.length === 0 ? 'No fantasy points data available' : 'No players found for the selected squad'}
                  </p>
                </div>
              )}
            </>
          )}
        </TabPanel>
      </div>

      {/* Member Detail Modal */}
      {selectedMember && (
        <div className="fixed inset-0 bg-black bg-opacity-50 dark:bg-opacity-70 flex items-center justify-center z-50 p-4" onClick={() => setSelectedMember(null)}>
          <div className="bg-white dark:bg-dark-400 rounded-lg shadow-xl max-w-2xl w-full max-h-[85vh] overflow-hidden border border-gray-200 dark:border-dark-300/40" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-dark-300/40 bg-blue-600 dark:bg-blue-700">
              <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                <FaUser />
                Member Details
              </h3>
              <button
                onClick={() => setSelectedMember(null)}
                className="text-white hover:bg-white hover:bg-opacity-20 rounded-full p-1 transition-colors"
              >
                <FiX className="h-5 w-5" />
              </button>
            </div>
            <div className="overflow-y-auto max-h-[calc(85vh-80px)] p-6">
              <div className="space-y-4">
                {selectedMember && Object.entries(selectedMember)
                  .filter(([key]) => key && !key.startsWith('_'))
                  .map(([key, value]) => {
                    const label = key
                      .replace(/([A-Z])/g, ' $1')
                      .replace(/_/g, ' ')
                      .replace(/^\w/, c => c.toUpperCase())
                      .trim();

                    let displayValue: React.ReactNode = String(value ?? 'N/A');

                    if (value === null || value === undefined) {
                      displayValue = <span className="text-gray-400 italic">N/A</span>;
                    } else if (typeof value === 'boolean') {
                      displayValue = (
                        <span className={`inline-flex px-2 py-1 rounded-full text-xs font-semibold ${
                          value ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                        }`}>
                          {value ? 'Yes' : 'No'}
                        </span>
                      );
                    } else if (typeof value === 'object') {
                      displayValue = (
                        <pre className="bg-gray-100 dark:bg-dark-300/40 rounded p-2 text-xs overflow-x-auto">
                          {JSON.stringify(value, null, 2)}
                        </pre>
                      );
                    } else if (key.toLowerCase().includes('date') || key.toLowerCase().includes('at')) {
                      displayValue = formatDate(String(value));
                    }

                    return (
                      <div key={key} className="flex flex-col sm:flex-row sm:items-start gap-1 sm:gap-4 py-2 border-b border-gray-100 dark:border-dark-300/20 last:border-0">
                        <span className="text-sm font-medium text-gray-500 dark:text-gray-400 sm:w-1/3 shrink-0">
                          {label}:
                        </span>
                        <span className="text-sm text-gray-900 dark:text-white break-all">
                          {displayValue}
                        </span>
                      </div>
                    );
                  })}
              </div>
            </div>
            <div className="p-4 border-t border-gray-200 dark:border-dark-300/40 flex justify-end">
              <button
                onClick={() => setSelectedMember(null)}
                className="px-4 py-2 bg-gray-100 dark:bg-dark-300/40 text-gray-700 dark:text-gray-300 rounded-lg font-medium hover:bg-gray-200 dark:hover:bg-dark-300/60 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Leaderboard Entry Detail Modal */}
      {selectedLeaderboardEntry && (
        <div className="fixed inset-0 bg-black bg-opacity-50 dark:bg-opacity-70 flex items-center justify-center z-50 p-4" onClick={() => setSelectedLeaderboardEntry(null)}>
          <div className="bg-white dark:bg-dark-400 rounded-lg shadow-xl max-w-2xl w-full max-h-[85vh] overflow-hidden border border-gray-200 dark:border-dark-300/40" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-dark-300/40 bg-yellow-500 dark:bg-yellow-600">
              <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                <FaTrophy />
                Leaderboard Entry Details
              </h3>
              <button
                onClick={() => setSelectedLeaderboardEntry(null)}
                className="text-white hover:bg-white hover:bg-opacity-20 rounded-full p-1 transition-colors"
              >
                <FiX className="h-5 w-5" />
              </button>
            </div>
            <div className="overflow-y-auto max-h-[calc(85vh-80px)] p-6">
              <div className="space-y-4">
                {selectedLeaderboardEntry && Object.entries(selectedLeaderboardEntry)
                  .filter(([key]) => key && !key.startsWith('_'))
                  .map(([key, value]) => {
                    const label = key
                      .replace(/([A-Z])/g, ' $1')
                      .replace(/_/g, ' ')
                      .replace(/^\w/, c => c.toUpperCase())
                      .trim();

                    let displayValue: React.ReactNode = String(value ?? 'N/A');

                    if (value === null || value === undefined) {
                      displayValue = <span className="text-gray-400 italic">N/A</span>;
                    } else if (typeof value === 'boolean') {
                      displayValue = (
                        <span className={`inline-flex px-2 py-1 rounded-full text-xs font-semibold ${
                          value ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                        }`}>
                          {value ? 'Yes' : 'No'}
                        </span>
                      );
                    } else if (typeof value === 'object') {
                      displayValue = (
                        <pre className="bg-gray-100 dark:bg-dark-300/40 rounded p-2 text-xs overflow-x-auto">
                          {JSON.stringify(value, null, 2)}
                        </pre>
                      );
                    } else if (key.toLowerCase().includes('date') || key.toLowerCase().includes('at')) {
                      displayValue = formatDate(String(value));
                    } else if (key.toLowerCase().includes('points') || key.toLowerCase().includes('score')) {
                      displayValue = (
                        <span className="font-bold text-blue-600 dark:text-blue-400 text-lg">{String(value)}</span>
                      );
                    }

                    return (
                      <div key={key} className="flex flex-col sm:flex-row sm:items-start gap-1 sm:gap-4 py-2 border-b border-gray-100 dark:border-dark-300/20 last:border-0">
                        <span className="text-sm font-medium text-gray-500 dark:text-gray-400 sm:w-1/3 shrink-0">
                          {label}:
                        </span>
                        <span className="text-sm text-gray-900 dark:text-white break-all">
                          {displayValue}
                        </span>
                      </div>
                    );
                  })}
              </div>
            </div>
            <div className="p-4 border-t border-gray-200 dark:border-dark-300/40 flex justify-end">
              <button
                onClick={() => setSelectedLeaderboardEntry(null)}
                className="px-4 py-2 bg-gray-100 dark:bg-dark-300/40 text-gray-700 dark:text-gray-300 rounded-lg font-medium hover:bg-gray-200 dark:hover:bg-dark-300/60 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Player Score Detail Modal */}
      {playerScoreDetailsModalOpen && selectedPlayerScore && (
        <div className="fixed inset-0 bg-black bg-opacity-50 dark:bg-opacity-70 flex items-center justify-center z-50 p-4" onClick={() => setPlayerScoreDetailsModalOpen(false)}>
          <div className="bg-white dark:bg-dark-400 rounded-lg shadow-xl max-w-6xl w-full max-h-[95vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-blue-600 to-blue-700 dark:from-blue-700 dark:to-blue-800 px-6 py-4 flex-shrink-0">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3 text-white">
                  <FaUser className="h-6 w-6" />
                  <div>
                    <h3 className="text-lg font-semibold">{selectedPlayerScore.playerName}</h3>
                    <p className="text-sm text-blue-100">Match by Match Breakdown</p>
                  </div>
                </div>
                <button
                  onClick={() => setPlayerScoreDetailsModalOpen(false)}
                  className="text-white hover:text-blue-100 transition-colors"
                >
                  <FiX className="h-6 w-6" />
                </button>
              </div>
            </div>

            {/* Modal Body */}
            <div className="flex-1 overflow-y-auto p-6">
              {selectedPlayerScore.matches && selectedPlayerScore.matches.length > 0 ? (
                <div className="space-y-4">
                  {selectedPlayerScore.matches.map((match: any, index: number) => {
                    const breakdown = match.scoresBreakdown || {};

                    return (
                      <div
                        key={index}
                        className="bg-white dark:bg-dark-300/40 border border-gray-200 dark:border-dark-300/40 rounded-lg p-4 shadow-sm hover:shadow-md transition-shadow"
                      >
                        <div className="flex items-center justify-between mb-3 pb-3 border-b border-gray-200 dark:border-dark-300/40">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <h4 className="font-semibold text-gray-900 dark:text-white">Match #{index + 1}</h4>
                              <span className="text-xs text-gray-500 dark:text-gray-400">ID: {match.matchId}</span>
                            </div>
                          </div>
                          {breakdown.mom ? (
                            <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-yellow-100 dark:bg-yellow-500/20 text-yellow-800 dark:text-yellow-400 border border-yellow-300 dark:border-yellow-500/30">
                              <FaTrophy className="h-3 w-3 mr-1" /> Man of the Match
                            </span>
                          ) : null}
                        </div>

                        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                          {/* Batting Stats */}
                          <div className="bg-blue-50 dark:bg-blue-500/10 p-3 rounded-lg">
                            <div className="text-xs text-gray-600 dark:text-gray-400 mb-1">Batting</div>
                            <div className="space-y-1">
                              <div className="flex justify-between text-sm">
                                <span className="text-gray-600 dark:text-gray-400">Runs:</span>
                                <span className="font-semibold text-gray-900 dark:text-white">{breakdown.runsScored || 0}</span>
                              </div>
                              <div className="flex justify-between text-sm">
                                <span className="text-gray-600 dark:text-gray-400">Balls:</span>
                                <span className="font-semibold text-gray-900 dark:text-white">{breakdown.ballsFaced || 0}</span>
                              </div>
                              <div className="flex justify-between text-sm">
                                <span className="text-gray-600 dark:text-gray-400">4s/6s:</span>
                                <span className="font-semibold text-gray-900 dark:text-white">{breakdown.fours || 0}/{breakdown.sixes || 0}</span>
                              </div>
                              <div className="flex justify-between text-sm">
                                <span className="text-gray-600 dark:text-gray-400">SR:</span>
                                <span className="font-semibold text-gray-900 dark:text-white">
                                  {breakdown.ballsFaced > 0 ? ((breakdown.runsScored / breakdown.ballsFaced) * 100).toFixed(2) : '0.00'}
                                </span>
                              </div>
                            </div>
                          </div>

                          {/* Bowling Stats */}
                          <div className="bg-green-50 dark:bg-green-500/10 p-3 rounded-lg">
                            <div className="text-xs text-gray-600 dark:text-gray-400 mb-1">Bowling</div>
                            <div className="space-y-1">
                              <div className="flex justify-between text-sm">
                                <span className="text-gray-600 dark:text-gray-400">Wickets:</span>
                                <span className="font-semibold text-gray-900 dark:text-white">{breakdown.wickets || 0}</span>
                              </div>
                              <div className="flex justify-between text-sm">
                                <span className="text-gray-600 dark:text-gray-400">Overs:</span>
                                <span className="font-semibold text-gray-900 dark:text-white">{breakdown.overs || 0}</span>
                              </div>
                              <div className="flex justify-between text-sm">
                                <span className="text-gray-600 dark:text-gray-400">Runs:</span>
                                <span className="font-semibold text-gray-900 dark:text-white">{breakdown.runsConceded || 0}</span>
                              </div>
                              <div className="flex justify-between text-sm">
                                <span className="text-gray-600 dark:text-gray-400">Econ:</span>
                                <span className="font-semibold text-gray-900 dark:text-white">
                                  {breakdown.overs > 0 ? (breakdown.runsConceded / breakdown.overs).toFixed(2) : '-'}
                                </span>
                              </div>
                            </div>
                          </div>

                          {/* Fielding Stats */}
                          <div className="bg-purple-50 dark:bg-purple-500/10 p-3 rounded-lg">
                            <div className="text-xs text-gray-600 dark:text-gray-400 mb-1">Fielding</div>
                            <div className="space-y-1">
                              <div className="flex justify-between text-sm">
                                <span className="text-gray-600 dark:text-gray-400">Catches:</span>
                                <span className="font-semibold text-gray-900 dark:text-white">{breakdown.catch || 0}</span>
                              </div>
                              <div className="flex justify-between text-sm">
                                <span className="text-gray-600 dark:text-gray-400">Stumps:</span>
                                <span className="font-semibold text-gray-900 dark:text-white">{breakdown.stump || 0}</span>
                              </div>
                              <div className="flex justify-between text-sm">
                                <span className="text-gray-600 dark:text-gray-400">Runouts:</span>
                                <span className="font-semibold text-gray-900 dark:text-white">{breakdown.runouts || 0}</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-12">
                  <FaChartBar className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">No Match Data</h3>
                  <p className="text-gray-600 dark:text-gray-400">No match-by-match breakdown available for this player</p>
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="px-6 py-4 bg-gray-50 dark:bg-dark-300/40 border-t border-gray-200 dark:border-dark-300/40 flex justify-end flex-shrink-0">
              <button
                onClick={() => setPlayerScoreDetailsModalOpen(false)}
                className="px-4 py-2 bg-gray-200 dark:bg-dark-300/60 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-300 dark:hover:bg-dark-300/80 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Fantasy Points Detail Modal */}
      {fantasyPointsDetailModalOpen && selectedFantasyPointsPlayer && (
        <div className="fixed inset-0 bg-black bg-opacity-50 dark:bg-opacity-70 flex items-center justify-center z-50 p-4" onClick={() => setFantasyPointsDetailModalOpen(false)}>
          <div className="bg-white dark:bg-dark-400 rounded-lg shadow-xl max-w-5xl w-full max-h-[95vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-indigo-600 to-indigo-700 dark:from-indigo-700 dark:to-indigo-800 px-6 py-4 flex-shrink-0">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3 text-white">
                  <FaChartBar className="h-6 w-6" />
                  <div>
                    <h3 className="text-lg font-semibold">{selectedFantasyPointsPlayer.playerName}</h3>
                    <p className="text-sm text-indigo-100">
                      {selectedFantasyPointsPlayer.squadName || ''} {selectedFantasyPointsPlayer.role ? `| ${selectedFantasyPointsPlayer.role}` : ''}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-white font-bold text-xl">{selectedFantasyPointsPlayer.totalPoints ?? 0} pts</span>
                  <button
                    onClick={() => setFantasyPointsDetailModalOpen(false)}
                    className="text-white hover:text-indigo-100 transition-colors"
                  >
                    <FiX className="h-6 w-6" />
                  </button>
                </div>
              </div>
              {/* Summary chips */}
              <div className="flex flex-wrap gap-2 mt-3">
                <span className="inline-flex px-2.5 py-1 rounded-full text-xs font-semibold bg-white/20 text-white">
                  {selectedFantasyPointsPlayer.matchesPlayed || 0} Matches
                </span>
                {selectedFantasyPointsPlayer.battingPoints !== 0 && (
                  <span className="inline-flex px-2.5 py-1 rounded-full text-xs font-semibold bg-blue-400/30 text-white">
                    Batting: {selectedFantasyPointsPlayer.battingPoints}
                  </span>
                )}
                {selectedFantasyPointsPlayer.bowlingPoints !== 0 && (
                  <span className="inline-flex px-2.5 py-1 rounded-full text-xs font-semibold bg-green-400/30 text-white">
                    Bowling: {selectedFantasyPointsPlayer.bowlingPoints}
                  </span>
                )}
                {selectedFantasyPointsPlayer.fieldingPoints !== 0 && (
                  <span className="inline-flex px-2.5 py-1 rounded-full text-xs font-semibold bg-purple-400/30 text-white">
                    Fielding: {selectedFantasyPointsPlayer.fieldingPoints}
                  </span>
                )}
                {selectedFantasyPointsPlayer.otherPoints !== 0 && (
                  <span className="inline-flex px-2.5 py-1 rounded-full text-xs font-semibold bg-yellow-400/30 text-white">
                    Bonus: {selectedFantasyPointsPlayer.otherPoints}
                  </span>
                )}
              </div>
            </div>

            {/* Modal Body */}
            <div className="flex-1 overflow-y-auto p-6">
              {selectedFantasyPointsPlayer.matches && selectedFantasyPointsPlayer.matches.length > 0 ? (
                <div className="space-y-4">
                  {selectedFantasyPointsPlayer.matches.map((match: any, index: number) => {
                    const matchPoints = match.totalPoints ?? 0;
                    // sections = [{ section, fields, total }]
                    const sections: any[] = match.sections || [];

                    const sectionColorMap: Record<string, { bg: string; border: string; header: string; text: string }> = {
                      'batting': { bg: 'bg-blue-50 dark:bg-blue-500/10', border: 'border-blue-200 dark:border-blue-500/30', header: 'text-blue-700 dark:text-blue-400', text: 'text-blue-600' },
                      'bowling': { bg: 'bg-green-50 dark:bg-green-500/10', border: 'border-green-200 dark:border-green-500/30', header: 'text-green-700 dark:text-green-400', text: 'text-green-600' },
                      'fielding': { bg: 'bg-purple-50 dark:bg-purple-500/10', border: 'border-purple-200 dark:border-purple-500/30', header: 'text-purple-700 dark:text-purple-400', text: 'text-purple-600' },
                      'other bonus': { bg: 'bg-amber-50 dark:bg-amber-500/10', border: 'border-amber-200 dark:border-amber-500/30', header: 'text-amber-700 dark:text-amber-400', text: 'text-amber-600' },
                    };

                    return (
                      <div
                        key={index}
                        className="bg-white dark:bg-dark-300/40 border border-gray-200 dark:border-dark-300/40 rounded-lg shadow-sm hover:shadow-md transition-shadow overflow-hidden"
                      >
                        {/* Match header */}
                        <div className="flex items-center justify-between px-4 py-3 bg-gray-50 dark:bg-dark-400/40 border-b border-gray-200 dark:border-dark-300/40">
                          <div>
                            <div className="flex items-center gap-2 mb-0.5">
                              <h4 className="font-semibold text-gray-900 dark:text-white">
                                {match.matchTitle || `Match #${index + 1}`}
                              </h4>
                              {match.matchDesc && (
                                <span className="text-xs text-gray-500 dark:text-gray-400">({match.matchDesc})</span>
                              )}
                            </div>
                            <div className="flex items-center gap-3 text-xs text-gray-500 dark:text-gray-400">
                              {match.venue && <span>{match.venue}</span>}
                              {match.status && <span>| {match.status}</span>}
                            </div>
                          </div>
                          <span className={`inline-flex items-center px-3 py-1.5 rounded-lg text-sm font-bold ${
                            matchPoints > 0
                              ? 'bg-green-100 dark:bg-green-500/20 text-green-800 dark:text-green-400'
                              : matchPoints < 0
                              ? 'bg-red-100 dark:bg-red-500/20 text-red-800 dark:text-red-400'
                              : 'bg-gray-100 dark:bg-gray-500/20 text-gray-600 dark:text-gray-400'
                          }`}>
                            {matchPoints > 0 ? '+' : ''}{matchPoints} pts
                          </span>
                        </div>

                        {/* Section breakdown cards */}
                        <div className="p-4">
                          {sections.length > 0 ? (
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                              {sections.map((sec: any, sIdx: number) => {
                                const sectionKey = (sec.section || '').toLowerCase();
                                const colors = sectionColorMap[sectionKey] || sectionColorMap['other bonus'];
                                const fields = sec.fields || {};
                                const fieldEntries = Object.entries(fields);
                                const sectionTotal = sec.total ?? 0;

                                return (
                                  <div key={sIdx} className={`${colors.bg} border ${colors.border} rounded-lg p-3`}>
                                    <div className="flex items-center justify-between mb-2">
                                      <span className={`text-xs font-bold uppercase tracking-wider ${colors.header}`}>
                                        {sec.section}
                                      </span>
                                      <span className={`text-sm font-bold ${sectionTotal > 0 ? colors.text : sectionTotal < 0 ? 'text-red-600' : 'text-gray-400'}`}>
                                        {sectionTotal > 0 ? '+' : ''}{sectionTotal}
                                      </span>
                                    </div>
                                    <div className="space-y-1">
                                      {fieldEntries.map(([key, val]) => {
                                        const numVal = Number(val);
                                        return (
                                          <div key={key} className="flex justify-between items-center text-xs">
                                            <span className="text-gray-600 dark:text-gray-400 capitalize">
                                              {key.replace(/_/g, ' ')}
                                            </span>
                                            <span className={`font-semibold ${
                                              numVal > 0 ? 'text-green-700 dark:text-green-400' : numVal < 0 ? 'text-red-600 dark:text-red-400' : 'text-gray-400'
                                            }`}>
                                              {numVal !== 0 ? (numVal > 0 ? `+${numVal}` : numVal) : '0'}
                                            </span>
                                          </div>
                                        );
                                      })}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          ) : (
                            <p className="text-sm text-gray-500 dark:text-gray-400">No breakdown details available for this match</p>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-12">
                  <FaChartBar className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">No Match Breakdown</h3>
                  <p className="text-gray-600 dark:text-gray-400">No match-by-match fantasy points breakdown available for this player</p>
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="px-6 py-4 bg-gray-50 dark:bg-dark-300/40 border-t border-gray-200 dark:border-dark-300/40 flex justify-end flex-shrink-0">
              <button
                onClick={() => setFantasyPointsDetailModalOpen(false)}
                className="px-4 py-2 bg-gray-200 dark:bg-dark-300/60 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-300 dark:hover:bg-dark-300/80 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Reassign Team Modal */}
      {reassignTeam && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setReassignTeam(null)}>
          <div className="bg-white dark:bg-dark-200 rounded-xl shadow-xl w-full max-w-md" onClick={e => e.stopPropagation()}>
            <div className="px-6 py-4 border-b border-gray-200 dark:border-dark-300/40">
              <h3 className="text-lg font-bold text-gray-900 dark:text-white">Reassign Team</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                Reassign <span className="font-semibold text-gray-700 dark:text-gray-300">{reassignTeam.fantasyName}</span> to a different member
              </p>
            </div>
            <div className="px-6 py-4 space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Current Owner</label>
                <p className="text-sm text-gray-500 dark:text-gray-400 font-mono">{reassignTeam.userId}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Select New Owner</label>
                <select
                  value={reassignUserId}
                  onChange={e => setReassignUserId(e.target.value)}
                  className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-dark-300/60 bg-white dark:bg-dark-300/40 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="">Select a member...</option>
                  {contestMembers
                    .filter((m: any) => m.userId !== reassignTeam.userId && m.status === 'accepted')
                    .map((m: any) => (
                      <option key={m.userId} value={m.userId}>
                        {m.firstname || m.username || m.userId} {m.lastname || ''} ({m.userId})
                      </option>
                    ))}
                  {/* Also include contest owner if different */}
                  {contest && (contest as any).ownerId !== reassignTeam.userId && (
                    <option value={(contest as any).ownerId}>Owner ({(contest as any).ownerId})</option>
                  )}
                </select>
              </div>
            </div>
            <div className="px-6 py-4 border-t border-gray-200 dark:border-dark-300/40 flex justify-end gap-3">
              <button
                onClick={() => setReassignTeam(null)}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-dark-300/40 rounded-lg hover:bg-gray-200 dark:hover:bg-dark-300/60 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  if (!reassignUserId) return;
                  setReassigning(true);
                  try {
                    const teamId = reassignTeam._id || reassignTeam.id;
                    await apiService.reassignFantasyTeam(teamId, reassignUserId);
                    // Refresh teams
                    const updated = await apiService.getContestFantasyTeams(contestId!);
                    setFantasyTeams(updated);
                    setReassignTeam(null);
                  } catch (err: any) {
                    alert(err.message || 'Failed to reassign');
                  } finally {
                    setReassigning(false);
                  }
                }}
                disabled={!reassignUserId || reassigning}
                className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                  !reassignUserId || reassigning
                    ? 'bg-gray-300 dark:bg-dark-300/40 text-gray-500 cursor-not-allowed'
                    : 'bg-primary-500 text-white hover:bg-primary-600'
                }`}
              >
                {reassigning ? 'Reassigning...' : 'Reassign'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ContestDetails;
